import { useCallback, useMemo } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";
import type {
  InterviewDisplay,
  InterviewerEntry,
  RelatedInterview,
  RsvpStatus,
} from "../types";

// ─── Query (calendarEvent detail from the legacy trackyr gateway) ───────

const INTERVIEW_DETAIL_QUERY = gql`
  query InterviewDetailQuery($eventId: ID!) {
    calendarEvent(eventId: $eventId) {
      id
      organisationId
      listing {
        id
        title
        company
        team {
          id
        }
      }
      application {
        id
        applicant {
          id
          name
          contactDetails {
            phone1
            email
          }
        }
      }
      candidate {
        id
        firstName
        lastName
        email
      }
      title
      description
      startsAt
      endsAt
      duration
      timezone
      isUnscheduled
      isCancelled
      location {
        description
        url
      }
      organiser {
        id
        firstName
        lastName
      }
      attendees {
        userId
        status
        user {
          firstName
          lastName
        }
      }
      permissions {
        update
        cancel
      }
    }
  }
`;

// ─── Query (sibling interviews for the same application/role) ───────────
// An "application" is one candidate applied to one listing, so its calendar
// events are exactly this candidate's interviews for this role. Used to fill
// the "N interviews for this role" strip on the detail header.

const INTERVIEW_SIBLINGS_QUERY = gql`
  query InterviewSiblingsQuery($applicationId: ID!) {
    calendarEvents(applicationId: $applicationId) {
      events {
        id
        title
        startsAt
        endsAt
        duration
        isUnscheduled
        isCancelled
      }
    }
  }
`;

// ─── Types for raw GQL response ────────────────────────────────────────

interface RawAttendee {
  userId: string;
  user?: { firstName: string; lastName: string };
  status: string;
}

interface RawEvent {
  id: string;
  organisationId: string;
  title: string;
  startsAt?: string;
  endsAt?: string;
  isUnscheduled?: boolean;
  duration: number;
  timezone?: string;
  description?: string;
  location?: { description?: string; url?: string };
  attendees: RawAttendee[];
  listing?: { id: string; title: string; company?: string };
  candidate?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  organiser?: { id: string; firstName: string; lastName: string };
  application?: {
    id: string;
    applicant?: {
      id: string;
      name: string;
      contactDetails?: { phone1?: string; email?: string };
    };
  };
  isCancelled?: boolean;
  permissions?: { update: boolean; cancel: boolean };
}

/** One row from the sibling-interviews list (a thinner CalendarEvent). */
interface RawSiblingEvent {
  id: string;
  title: string;
  startsAt?: string;
  endsAt?: string;
  duration: number;
  isUnscheduled?: boolean;
  isCancelled?: boolean;
}

// ─── Mapper ────────────────────────────────────────────────────────────

function mapRsvp(status: string): RsvpStatus {
  switch (status) {
    case "accepted":
      return "Confirmed";
    default:
      return "Pending";
  }
}

/** The lifecycle status derives only from timing/cancellation, so it works for
 *  the full detail event and the thinner sibling rows alike. */
type EventTiming = Pick<
  RawEvent,
  "isCancelled" | "startsAt" | "endsAt" | "duration" | "isUnscheduled"
>;

function mapStatus(
  event: EventTiming,
): "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED" {
  if (event.isCancelled) return "CANCELLED";
  if (!event.startsAt || event.isUnscheduled) return "SCHEDULED";
  const start = new Date(event.startsAt);
  const end = event.endsAt
    ? new Date(event.endsAt)
    : new Date(start.getTime() + event.duration * 60000);
  const now = new Date();
  if (now > end) return "COMPLETED";
  if (now >= start && now <= end) return "IN_PROGRESS";
  return "SCHEDULED";
}

function toInterviewDisplay(
  event: RawEvent,
  currentUserId?: string | null,
): InterviewDisplay {
  const candidateName = event.candidate
    ? `${event.candidate.firstName} ${event.candidate.lastName}`
    : (event.application?.applicant?.name ?? "Unknown Candidate");

  const organiserName = event.organiser
    ? `${event.organiser.firstName} ${event.organiser.lastName}`
    : undefined;

  const status = mapStatus(event);

  // Editability mirrors the legacy interview page: only an interview that
  // hasn't started yet can be edited or cancelled. Lifecycle is the first
  // gate, role permission the second — BOTH must hold:
  //   • SCHEDULED (future, or not-yet-scheduled) → editable
  //   • IN_PROGRESS / COMPLETED (started or finished, by start/end time) → read-only
  //   • CANCELLED → read-only
  // The backend grants `permissions.update`/`.cancel` by role only (it doesn't
  // know the date), so the date/completed check has to live here on top of it.
  const isOpenForChanges = status === "SCHEDULED";
  const canEditDetails = isOpenForChanges && !!event.permissions?.update;
  const canCancel = isOpenForChanges && !!event.permissions?.cancel;

  // The viewer's own attendee row drives the RSVP ("Going?") control. Absent
  // from the panel → undefined → no control shown.
  const myAttendee = currentUserId
    ? event.attendees.find((a) => a.userId === currentUserId)
    : undefined;
  const myRsvpStatus = myAttendee ? myAttendee.status : undefined;

  return {
    id: event.id,
    candidateName,
    candidateId: event.candidate?.id ?? event.application?.applicant?.id ?? "",
    applicationId: event.application?.id,
    listingId: event.listing?.id,
    // The match/applicant "legacyId" — the id trackyr's `applicant(id:)` takes to
    // return the candidate's organisational-fit assessments. Distinct from
    // candidateId (the person) and applicationId (the application).
    applicantId: event.application?.applicant?.id,
    candidateEmail:
      event.candidate?.email ??
      event.application?.applicant?.contactDetails?.email,
    candidatePhone: event.application?.applicant?.contactDetails?.phone1,
    jobTitle: event.listing?.title,
    organiser: organiserName,
    scheduledAt: event.startsAt ?? new Date().toISOString(),
    durationMinutes: event.duration,
    interviewPurpose: event.title,
    status,
    location: event.location?.description,
    meetingLink: event.location?.url,
    description: event.description,
    timezone: event.timezone,
    myRsvpStatus,
    interviewers: event.attendees.map(
      (a): InterviewerEntry => ({
        id: a.userId,
        user: {
          id: a.userId,
          name: a.user ? `${a.user.firstName} ${a.user.lastName}` : "Unknown",
        },
        rsvpStatus: mapRsvp(a.status),
      }),
    ),
    assignedGuide: null,
    transcript: null,
    canEditDetails,
    canCancel,
  };
}

/** Short pill label for the related-interviews strip, e.g. "18 Jun". */
function formatShortDate(startsAt?: string): string {
  if (!startsAt) return "TBC";
  return new Date(startsAt).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
}

function toRelatedInterviews(
  events: RawSiblingEvent[],
  currentId: string,
): RelatedInterview[] {
  return (
    events
      // A cancelled sibling isn't a live interview for the role; keep only the
      // current one even if it's been cancelled, so the strip stays anchored.
      .filter((e) => !e.isCancelled || e.id === currentId)
      // Chronological, with unscheduled (no start) last.
      .sort((a, b) => {
        if (!a.startsAt) return 1;
        if (!b.startsAt) return -1;
        return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
      })
      .map((e) => ({
        id: e.id,
        name: e.title,
        date: formatShortDate(e.startsAt),
        current: e.id === currentId,
        completed: mapStatus(e) === "COMPLETED",
      }))
  );
}

// ─── Hook: useInterviewDetail ──────────────────────────────────────────

export function useInterviewDetail(
  interviewId: string | null,
  currentUserId?: string | null,
) {
  const { trackyrClient } = useApolloClients();

  const { data, loading, error, refetch } = useQuery(INTERVIEW_DETAIL_QUERY, {
    client: trackyrClient,
    variables: { eventId: interviewId },
    skip: !interviewId,
    fetchPolicy: "cache-and-network",
  });

  // The candidate's other interviews for this role share the application. Fetch
  // them once the detail event resolves the application id.
  const applicationId: string | undefined =
    data?.calendarEvent?.application?.id;
  const { data: siblingsData } = useQuery(INTERVIEW_SIBLINGS_QUERY, {
    client: trackyrClient,
    variables: { applicationId },
    skip: !applicationId,
    fetchPolicy: "cache-and-network",
  });

  const interview: InterviewDisplay | null = useMemo(() => {
    if (!data?.calendarEvent) return null;
    const display = toInterviewDisplay(data.calendarEvent, currentUserId);
    const siblings: RawSiblingEvent[] =
      siblingsData?.calendarEvents?.events ?? [];
    const related = toRelatedInterviews(siblings, data.calendarEvent.id);
    // Only surface the strip when there's genuinely more than one interview for
    // the role — a single interview doesn't need a switcher.
    return related.length > 1
      ? { ...display, relatedInterviews: related }
      : display;
  }, [data?.calendarEvent, siblingsData, currentUserId]);

  return { interview, loading, error, refetch };
}

// ─── Hook: useInterviewRsvp (the "Going?" Yes/No control) ──────────────
// Reuses the legacy trackyr mutation; selecting the same attendee fields the
// detail query holds keeps Apollo's normalised CalendarEvent in sync (the
// "Going?" buttons flip without a refetch).

const RSVP_MUTATION = gql`
  mutation InterviewRsvp($eventId: ID!, $status: String!) {
    updateCalendarEventResponse(eventId: $eventId, status: $status) {
      id
      attendees {
        userId
        status
        user {
          firstName
          lastName
        }
      }
    }
  }
`;

export function useInterviewRsvp() {
  const { trackyrClient } = useApolloClients();
  const [rsvpMutation, { loading }] = useMutation(RSVP_MUTATION, {
    client: trackyrClient,
  });

  const respond = useCallback(
    (interviewId: string, status: "accepted" | "declined") =>
      rsvpMutation({ variables: { eventId: interviewId, status } }),
    [rsvpMutation],
  );

  return { respond, loading };
}
